/**
 * The student-facing recording state machine.
 *
 * The recorder self-heals (see AttemptRecorder + useCamera): when a track dies
 * it re-acquires the camera and resumes. That is invisible on its own — and the
 * whole point of the recording UX is that the student must never be left in a
 * silent stop. This derives a single, honest state from the real MediaRecorder
 * and camera state so both devices can show it (and the phone can beep on it):
 *
 *   recording     — the MediaRecorder is actively producing video
 *   reconnecting  — recording is wanted but paused; the camera is being
 *                   re-acquired (transient, self-heal in flight)
 *   stopped       — recording is wanted but the camera is denied/absent/failed,
 *                   or the reconnect has dragged on past the grace window; this
 *                   needs the student to act, and is shown prominently
 *   idle          — recording is not wanted (before the exam / after submit)
 */
import { useEffect, useRef, useState } from "react";
import type { CameraState } from "./useCamera";
import { beepConfirm, beepError } from "./beeps";

export type RecordingUiState = "idle" | "recording" | "reconnecting" | "stopped";

/**
 * How long a wanted-but-not-recording gap may last before we stop calling it a
 * transient "reconnecting" and warn that recording has stopped. Self-heal is
 * near-instant when it works, so a gap this long means it is not coming back on
 * its own.
 */
const RECONNECT_GRACE_MS = 15_000;

export interface RecordingUi {
  state: RecordingUiState;
  /** ms since recording first began this session, or null before it ever did. */
  elapsedMs: number | null;
}

export function useRecordingUi(input: {
  /** True once recording is meant to be happening. */
  wanted: boolean;
  /** Live camera acquisition state (from useCamera). */
  cameraState: CameraState;
  /** Whether the MediaRecorder is actively recording right now. */
  recording: boolean;
  /**
   * The recorder says it is recording but has produced nothing for a while.
   *
   * This has to be a separate input because `MediaRecorder.state` keeps
   * reading "recording" through exactly the failure that matters: the encoder
   * goes quiet, the camera preview keeps rendering, and the badge in the
   * corner sits on a confident green REC while nothing at all is being saved.
   * Treating a stall as not-recording is what makes the indicator honest.
   */
  stalled?: boolean;
}): RecordingUi {
  const { wanted, cameraState, stalled = false } = input;
  const recording = input.recording && !stalled;

  const startedAtRef = useRef<number | null>(null);
  const lostSinceRef = useRef<number | null>(null);
  // Re-render on a timer so elapsed ticks and the grace window can escalate
  // reconnecting → stopped even while nothing else changes.
  const [, forceTick] = useState(0);

  // First moment recording actually began — anchors the elapsed clock.
  if (recording && startedAtRef.current === null) {
    startedAtRef.current =
      typeof performance !== "undefined" ? performance.now() : 0;
  }
  // Track how long we have been wanted-but-not-recording, for the grace window.
  const now = typeof performance !== "undefined" ? performance.now() : 0;
  if (wanted && !recording) {
    if (lostSinceRef.current === null) lostSinceRef.current = now;
  } else {
    lostSinceRef.current = null;
  }

  useEffect(() => {
    if (!wanted) return;
    const id = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [wanted]);

  let state: RecordingUiState;
  if (!wanted) {
    state = "idle";
  } else if (recording) {
    state = "recording";
  } else if (
    cameraState === "denied" ||
    cameraState === "unavailable" ||
    cameraState === "error"
  ) {
    state = "stopped";
  } else {
    const lostFor =
      lostSinceRef.current !== null ? now - lostSinceRef.current : 0;
    state = lostFor > RECONNECT_GRACE_MS ? "stopped" : "reconnecting";
  }

  const elapsedMs =
    startedAtRef.current === null ? null : Math.max(0, now - startedAtRef.current);

  return { state, elapsedMs };
}

/**
 * Plays a confirmation tone whenever recording becomes live (first start or a
 * recovery) and an error tone whenever it drops out of recording. Intended for
 * the phone, whose screen is not being watched. No-op until `unlockBeeps()` has
 * run inside a gesture.
 */
export function useRecordingBeeps(state: RecordingUiState): void {
  const prev = useRef<RecordingUiState | null>(null);
  useEffect(() => {
    const before = prev.current;
    prev.current = state;
    if (before === null || before === state) return;
    if (state === "recording") {
      beepConfirm(); // started, or recovered from a drop
    } else if (before === "recording") {
      beepError(); // recording → reconnecting/stopped
    }
  }, [state]);
}
